// Spec-bearing gamepad-input state machine, shared by every client
// embedding (browser MunosClient, headless dummy client). The rules it
// implements come from docs/language.md §Input:
//
//   - `event input` fires synchronously on each edge; the script may
//     call consume_input() only while that handler is on the stack.
//   - input_held / input_pressed poll a FRAME-START snapshot, so every
//     poll within one `event frame` sees the same view.
//   - A consumed press auto-consumes its matching release — the ROM
//     sees neither half of the edge pair.
//
// The latch is transport- and wasm-agnostic (browser-safe, zero deps):
// the actual event_client_input invocation is injected via
// `dispatch`, and the host's between-handlers callback drain via
// `afterDispatch`. afterDispatch runs after the in-handler flag has
// been cleared and the consumed-press bookkeeping is done — matching
// the hosts' historical ordering, where a consume_input() reached from
// a drained db/fetch callback is an out-of-handler error, not a
// consume.
export class InputLatch {
    hooks;
    // Live set, mutated by press/release.
    heldButtons = new Set();
    // Rising edges accumulated since the last frame snapshot.
    pressedSinceLastFrame = new Set();
    // Frame-start snapshots that isHeld/isPressed read — stable across
    // all polls within one frame's handler.
    frameStartHeld = new Set();
    frameStartPressed = new Set();
    // Buttons whose press was consumed; their release is auto-consumed.
    consumedHeldButtons = new Set();
    inputEventActive = false;
    currentEdgeConsumed = false;
    constructor(hooks) {
        this.hooks = hooks;
    }
    // Simulate a controller edge. Returns whether the script consumed it
    // (true = the embedding should suppress the matching forward to the
    // underlying ROM). No-op if the button is already held — real
    // gamepads don't repeat-fire edges.
    press(button) {
        if (this.heldButtons.has(button))
            return false;
        this.heldButtons.add(button);
        this.pressedSinceLastFrame.add(button);
        return this.dispatchEdge(button, true);
    }
    release(button) {
        if (!this.heldButtons.has(button))
            return false;
        this.heldButtons.delete(button);
        const pressWasConsumed = this.consumedHeldButtons.delete(button);
        const userConsumed = this.dispatchEdge(button, false);
        return pressWasConsumed || userConsumed;
    }
    // Take the frame-start snapshot. Call at the top of each frame,
    // before event_client_frame; the next frame sees only edges that
    // arrive after this point.
    beginFrame() {
        this.frameStartHeld = new Set(this.heldButtons);
        this.frameStartPressed = new Set(this.pressedSinceLastFrame);
        this.pressedSinceLastFrame.clear();
    }
    // Polling surface for the input_held / input_pressed env imports.
    isHeld(button) { return this.frameStartHeld.has(button); }
    isPressed(button) { return this.frameStartPressed.has(button); }
    // Live (un-snapshotted) held query for host-side convenience APIs.
    isHeldLive(button) { return this.heldButtons.has(button); }
    // Backs the consume_input env import. Returns false when no input
    // handler is on the stack (the host logs the script error); the call
    // is ignored in that case.
    consume() {
        if (!this.inputEventActive)
            return false;
        this.currentEdgeConsumed = true;
        return true;
    }
    // Full reset — for hosts that re-instantiate the wasm (inter-
    // deployment hop) and must not leak input state into the new script.
    reset() {
        this.heldButtons.clear();
        this.pressedSinceLastFrame.clear();
        this.frameStartHeld = new Set();
        this.frameStartPressed = new Set();
        this.consumedHeldButtons.clear();
        this.inputEventActive = false;
        this.currentEdgeConsumed = false;
    }
    dispatchEdge(button, pressed) {
        this.currentEdgeConsumed = false;
        this.inputEventActive = true;
        try {
            this.hooks.dispatch(button, pressed);
        }
        finally {
            this.inputEventActive = false;
        }
        if (pressed && this.currentEdgeConsumed) {
            this.consumedHeldButtons.add(button);
        }
        this.hooks.afterDispatch?.();
        return this.currentEdgeConsumed;
    }
}
//# sourceMappingURL=input-latch.js.map